import { useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  COUNTRY_DIALS,
  DEFAULT_DIAL,
  OTHER_DIAL_KEY,
  splitPhone,
  joinPhone,
} from '@hotel/shared/utils'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

type PhoneInputProps = {
  id?: string
  value: string
  onChange: (next: string) => void
  placeholder?: string
  disabled?: boolean
}

const selectClass =
  'h-10 shrink-0 rounded-md border border-cream-dark bg-white px-2 text-sm ring-offset-cream-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'

export function PhoneInput({
  id,
  value,
  onChange,
  placeholder = '9999-1111',
  disabled,
}: PhoneInputProps) {
  const autoId = useId()
  const inputId = id || autoId

  const initial = useMemo(() => splitPhone(value), []) // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedDial, setSelectedDial] = useState(
    initial.isOther ? OTHER_DIAL_KEY : initial.dial,
  )
  const [otherDial, setOtherDial] = useState(initial.isOther ? initial.dial : '')
  const [local, setLocal] = useState(initial.local)

  const lastEmittedRef = useRef(value)
  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      const next = splitPhone(value)
      setSelectedDial(next.isOther ? OTHER_DIAL_KEY : next.dial)
      setOtherDial(next.isOther ? next.dial : '')
      setLocal(next.local)
      lastEmittedRef.current = value
    }
  }, [value])

  const emit = (s: string, o: string, l: string) => {
    const dial = s === OTHER_DIAL_KEY ? o || '+' : s
    const v = joinPhone(dial, l)
    lastEmittedRef.current = v
    onChange(v)
  }

  const handleCountryChange = (next: string) => {
    setSelectedDial(next)
    if (next !== OTHER_DIAL_KEY) setOtherDial('')
    emit(next, otherDial, local)
  }

  const handleOtherDialChange = (raw: string) => {
    const cleaned = '+' + raw.replace(/[^\d]/g, '').slice(0, 3)
    setOtherDial(cleaned)
    emit(OTHER_DIAL_KEY, cleaned, local)
  }

  const handleLocalChange = (raw: string) => {
    setLocal(raw)
    emit(selectedDial, otherDial, raw)
  }

  const isOtherSelected = selectedDial === OTHER_DIAL_KEY
  const selectValue = selectedDial || DEFAULT_DIAL

  return (
    <div className="flex gap-2">
      <select
        className={cn(selectClass, 'w-[140px]')}
        value={selectValue}
        onChange={(e) => handleCountryChange(e.target.value)}
        disabled={disabled}
        aria-label="Código de país"
      >
        {COUNTRY_DIALS.map((c) => (
          <option key={c.iso} value={c.dial}>
            {c.flag} {c.dial}
          </option>
        ))}
        <option value={OTHER_DIAL_KEY}>🌐 Otro…</option>
      </select>
      {isOtherSelected ? (
        <Input
          value={otherDial}
          onChange={(e) => handleOtherDialChange(e.target.value)}
          placeholder="+CC"
          className="w-[80px] shrink-0"
          disabled={disabled}
          inputMode="numeric"
        />
      ) : null}
      <Input
        id={inputId}
        value={local}
        onChange={(e) => handleLocalChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        inputMode="tel"
        autoComplete="tel-national"
      />
    </div>
  )
}
